// @ts-nocheck
import { useState, useMemo, useCallback } from "react";

export type HawkeyeSortField = "account_name" | "overallScore" | string; // string for category names
export type SortDirection = "asc" | "desc";

export interface HawkeyeSortConfig {
  field: HawkeyeSortField;
  direction: SortDirection;
}

export interface UseHawkeyeSortingResult {
  sortedData: any[];
  sortConfig: HawkeyeSortConfig | null;
  handleSort: (field: HawkeyeSortField) => void;
  setSortConfig: (config: HawkeyeSortConfig | null) => void;
}

const getAccountNameForSort = (account: any) =>
  String(account?.account_name || account?.name || "").trim().toLowerCase();

const getSponsorNameForSort = (account: any) =>
  String(account?.primary_sponsors || "").trim() || "Unassigned";

const isUnassignedForSort = (account: any) => {
  const primarySponsorId = String(account?.primary_sponsors_id || "").trim();
  const secondarySponsorIds = account?.secondary_sponsors_id
    ? String(account.secondary_sponsors_id)
        .split(",")
        .map((id: string) => id.trim())
        .filter(Boolean)
    : [];
  const primarySponsorName = String(account?.primary_sponsors || "").trim();
  const secondarySponsorName = String(account?.secondary_sponsors || "").trim();

  if (primarySponsorId || secondarySponsorIds.length > 0) {
    return false;
  }

  return !primarySponsorName && !secondarySponsorName;
};

/** Accounts with no Enterprise KPI data always sort after accounts that have data. */
const compareByHasData = (left: any, right: any) => {
  const leftNoData = left?.hasData === false;
  const rightNoData = right?.hasData === false;
  if (leftNoData === rightNoData) return 0;
  return leftNoData ? 1 : -1;
};

const compareByAccountName = (left: any, right: any) =>
  getAccountNameForSort(left).localeCompare(getAccountNameForSort(right), undefined, {
    sensitivity: "base",
  });

const compareByAccountListOrder = (
  left: any,
  right: any,
  prioritizeByKpiData = true
) => {
  if (prioritizeByKpiData) {
    const hasDataCompare = compareByHasData(left, right);
    if (hasDataCompare !== 0) {
      return hasDataCompare;
    }

    const leftUnassigned = isUnassignedForSort(left);
    const rightUnassigned = isUnassignedForSort(right);

    if (leftUnassigned !== rightUnassigned) {
      return leftUnassigned ? -1 : 1;
    }

    const sponsorCompare = getSponsorNameForSort(left).localeCompare(
      getSponsorNameForSort(right),
      undefined,
      { sensitivity: "base" }
    );

    if (sponsorCompare !== 0) {
      return sponsorCompare;
    }
  }

  return compareByAccountName(left, right);
};

const prioritizeSelectedAccounts = (
  left: any,
  right: any,
  prioritizedAccountIds: string[]
) => {
  if (prioritizedAccountIds.length === 0) return 0;

  const prioritizedIds = new Set(prioritizedAccountIds);
  const leftSelected = prioritizedIds.has(String(left?.account_id || ""));
  const rightSelected = prioritizedIds.has(String(right?.account_id || ""));

  if (leftSelected === rightSelected) return 0;
  return leftSelected ? -1 : 1;
};

export const useHawkeyeSorting = (
  data: any[],
  prioritizedAccountIds: string[] = [],
  /** When true, accounts with KPI data sort above accounts without. When false, alphabetical. */
  prioritizeByKpiData = true
): UseHawkeyeSortingResult => {
  const [sortConfig, setSortConfig] = useState<HawkeyeSortConfig | null>(null);

  const sortedData = useMemo(() => {
    if (!sortConfig) {
      return [...data].sort((left, right) => {
        const selectedCompare = prioritizeSelectedAccounts(
          left,
          right,
          prioritizedAccountIds
        );

        if (selectedCompare !== 0) {
          return selectedCompare;
        }

        return compareByAccountListOrder(left, right, prioritizeByKpiData);
      });
    }

    const { field, direction } = sortConfig;

    return [...data].sort((a, b) => {
      const selectedCompare = prioritizeSelectedAccounts(
        a,
        b,
        prioritizedAccountIds
      );

      if (selectedCompare !== 0) {
        return selectedCompare;
      }

      // Categories + Metrics: accounts without KPI data stay at the bottom when sorting by data
      if (prioritizeByKpiData) {
        const hasDataCompare = compareByHasData(a, b);
        if (hasDataCompare !== 0) {
          return hasDataCompare;
        }
      }

      let aValue: any;
      let bValue: any;

      // Handle different sort fields
      switch (field) {
        case "account_name":
          aValue = a.account_name.toLowerCase();
          bValue = b.account_name.toLowerCase();
          break;
        case "overallScore":
          aValue = a.overallScore;
          bValue = b.overallScore;
          break;
        default:
          // Category / metric column sorting
          const aCategory = a.categoryScores.find(cs => cs.category === field);
          const bCategory = b.categoryScores.find(cs => cs.category === field);

          aValue = aCategory?.score || 0;
          bValue = bCategory?.score || 0;
          break;
      }

      // Perform comparison
      let comparison = 0;
      if (aValue < bValue) {
        comparison = -1;
      } else if (aValue > bValue) {
        comparison = 1;
      }

      return direction === "desc" ? -comparison : comparison;
    });
  }, [data, sortConfig, prioritizedAccountIds, prioritizeByKpiData]);

  const handleSort = useCallback((field: HawkeyeSortField) => {
    setSortConfig(currentConfig => {
      if (!currentConfig || currentConfig.field !== field) {
        // First click: sort desc
        return { field, direction: "desc" };
      } else if (currentConfig.direction === "desc") {
        // Second click: sort asc
        return { field, direction: "asc" };
      } else {
        // Third click: clear sort
        return null;
      }
    });
  }, []);

  return {
    sortedData,
    sortConfig,
    handleSort,
    setSortConfig,
  };
};
